import {
  Button,
  IconNotification,
  Input,
  ToggleBase,
} from "@light-technology/components";
import { Mail01, Phone01 } from "@untitledui/icons";
import { AsYouType } from "libphonenumber-js";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { api } from "../../state/api";
import type {
  AppConfigurationResponse,
  AppResponse,
} from "../../state/api.types";
import { extractFieldErrors } from "../../utilities/errors";
import { BasicModal } from "../shared/BasicModal";
import { FormCard } from "../shared/FormCard";

type SalesContactForm = {
  partners_support_email: string;
  partner_sales_phone: string;
};

type AppearanceSupportCardProps = {
  app: AppResponse;
  configuration: AppConfigurationResponse;
};

function formatPhone(value: string | null): string {
  if (!value) return "";
  return new AsYouType("US").input(value);
}

function hasCustomContact(configuration: AppConfigurationResponse): boolean {
  return (
    !!configuration.partners_support_email ||
    !!configuration.partner_sales_phone
  );
}

export const AppearanceSupportCard = (props: AppearanceSupportCardProps) => {
  const [isCustomized, setIsCustomized] = useState(
    hasCustomContact(props.configuration),
  );

  const [updateConfiguration, updateConfigurationResult] =
    api.useUpdateConfigurationMutation();

  const {
    handleSubmit,
    reset,
    control,
    setError,
    formState: { isDirty },
  } = useForm<SalesContactForm>({
    defaultValues: {
      partners_support_email: props.configuration.partners_support_email ?? "",
      partner_sales_phone: formatPhone(props.configuration.partner_sales_phone),
    },
    mode: "onChange",
  });

  const showSuccessToast = () => {
    toast.custom((t) => (
      <IconNotification
        color="success"
        title="Success"
        description="Sales contact details have been updated"
        hideDismissLabel
        onClose={() => toast.dismiss(t)}
      />
    ));
  };

  const handleApiError = (error: unknown) => {
    const fieldErrors = extractFieldErrors(error);
    if (fieldErrors.length > 0) {
      for (const { field, message } of fieldErrors) {
        if (
          field === "partners_support_email" ||
          field === "partner_sales_phone"
        ) {
          setError(field, { type: "server", message });
        }
      }
    } else {
      toast.custom((t) => (
        <IconNotification
          color="error"
          title="Something went wrong."
          description="Please try again later."
          hideDismissLabel
          onClose={() => toast.dismiss(t)}
        />
      ));
    }
  };

  const onSubmit = handleSubmit(
    ({ partners_support_email, partner_sales_phone }) => {
      updateConfiguration({
        appUUID: props.app.uuid,
        company_name: props.configuration.company_name,
        partners_support_email: partners_support_email,
        partner_sales_phone: partner_sales_phone,
      })
        .unwrap()
        .then(() => {
          reset({ partners_support_email, partner_sales_phone });
          showSuccessToast();
        })
        .catch(handleApiError);
    },
  );

  const [showConfirm, setShowConfirm] = useState(false);

  const handleToggle = (selected: boolean) => {
    if (!selected) {
      if (hasCustomContact(props.configuration)) {
        setShowConfirm(true);
        return;
      }
      reset({ partners_support_email: "", partner_sales_phone: "" });
      setIsCustomized(false);
    } else {
      setIsCustomized(true);
    }
  };

  const handleConfirmClear = () => {
    updateConfiguration({
      appUUID: props.app.uuid,
      company_name: props.configuration.company_name,
      partners_support_email: "",
      partner_sales_phone: "",
    })
      .unwrap()
      .then(() => {
        setIsCustomized(false);
        setShowConfirm(false);
        reset({ partners_support_email: "", partner_sales_phone: "" });
        showSuccessToast();
      })
      .catch(handleApiError);
  };

  const isToggleLoading = updateConfigurationResult.isLoading && showConfirm;

  return (
    <>
      <BasicModal
        isOpen={showConfirm}
        onOpenChange={setShowConfirm}
        textTitle="Are you sure want to switch to the Light support team as your sales team contact?"
        textDescription="Keeping a customized sales team contact can let your own team speak directly about your products."
        textCancel="Cancel"
        textSubmit="Yes, switch"
        isDestructive
        isDisabled={updateConfigurationResult.isLoading}
        isLoading={updateConfigurationResult.isLoading}
        onCancel={() => {
          setShowConfirm(false);
        }}
        onSubmit={handleConfirmClear}
        data-ph-capture-attribute-action="clear-sales-contact"
      />
      <FormCard
        name="Sales team contact"
        subtext={
          isCustomized
            ? "Customize the contact information customers see in enrollment and renewal reminders."
            : "Customize the contact information customers see in enrollment and renewal reminders. When disabled, customers are directed to the Light support team."
        }
        headerAction={
          /* TODO: Replace with library <Toggle> once AriaSwitch blank-screen crash is resolved.
              AriaSwitch's flushSync causes the page to silently unmount in React 19 + StrictMode. */
          <button
            type="button"
            role="switch"
            aria-checked={isCustomized}
            aria-label={isCustomized ? "Customized" : "Default"}
            disabled={isToggleLoading}
            onClick={() => {
              handleToggle(!isCustomized);
            }}
            className="flex shrink-0 cursor-pointer items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-sm text-tertiary">
              {isCustomized ? "Customized" : "Default"}
            </span>
            <ToggleBase
              isSelected={isCustomized}
              isDisabled={isToggleLoading}
              size="sm"
            />
          </button>
        }
        customButton={
          isCustomized ? (
            <Button
              color="primary"
              size="md"
              isDisabled={!isDirty}
              isLoading={updateConfigurationResult.isLoading}
              onClick={onSubmit}
              data-ph-capture-attribute-action="update-sales-contact"
            >
              Save details
            </Button>
          ) : (
            <></>
          )
        }
      >
        <div className="flex gap-3xl">
          <div className="flex flex-1 flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">
              Email address
            </span>
            <Controller
              control={control}
              name="partners_support_email"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="email"
                  placeholder={
                    isCustomized ? "" : props.configuration.support_email
                  }
                  aria-label="Sales email address"
                  icon={Mail01}
                  validationBehavior="aria"
                  isInvalid={fieldState.invalid}
                  hint={fieldState.error?.message}
                  isDisabled={!isCustomized}
                />
              )}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">
              Phone number
            </span>
            <Controller
              control={control}
              name="partner_sales_phone"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  value={formatPhone(field.value)}
                  onChange={(v) => {
                    field.onChange(formatPhone(v));
                  }}
                  type="tel"
                  placeholder={
                    isCustomized
                      ? ""
                      : formatPhone(props.configuration.support_phone)
                  }
                  aria-label="Sales phone number"
                  icon={Phone01}
                  validationBehavior="aria"
                  isInvalid={fieldState.invalid}
                  hint={fieldState.error?.message}
                  isDisabled={!isCustomized}
                />
              )}
            />
          </div>
        </div>
      </FormCard>
    </>
  );
};
